import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { JOURNAL_PHRASE_POPOVER_Z_CLASS } from '@/lib/journal/journalOverlayLayers';
import { FORM_ERROR_TYPE, FORM_HINT_TYPE, FORM_LABEL_TYPE, TYPE } from '@/lib/typography';
import { useAuth } from '@/lib/auth';
import {
  useCreateJournalPhrase,
  useJournalPhrases,
  useUpdateJournalPhrase,
  type JournalPhraseRow,
} from '@/hooks/useJournalPhrases';
import {
  canManageJournalPhrase,
  detectSlashTrigger,
  filterJournalPhrases,
  insertPhraseText,
  isPhraseShortcutConflict,
  phraseVisibilityLabel,
  phrasesForSlashPopup,
  type SlashTrigger,
} from '@/utils/journalPhrases';
import { JournalPhraseFormModal, type JournalPhraseFormValues } from './JournalPhraseFormModal';
import { ManageJournalPhrasesModal } from './ManageJournalPhrasesModal';

interface CaretBox {
  top: number;
  left: number;
  height: number;
}

function getCaretCoordinates(element: HTMLTextAreaElement, position: number): CaretBox {
  const style = window.getComputedStyle(element);
  const mirror = document.createElement('div');
  const properties = [
    'direction',
    'boxSizing',
    'width',
    'height',
    'overflowX',
    'overflowY',
    'borderTopWidth',
    'borderRightWidth',
    'borderBottomWidth',
    'borderLeftWidth',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'fontStyle',
    'fontVariant',
    'fontWeight',
    'fontStretch',
    'fontSize',
    'lineHeight',
    'fontFamily',
    'textAlign',
    'textTransform',
    'textIndent',
    'textDecoration',
    'letterSpacing',
    'wordSpacing',
    'tabSize',
  ] as const;
  for (const prop of properties) {
    mirror.style.setProperty(prop, style.getPropertyValue(prop));
  }
  mirror.style.position = 'absolute';
  mirror.style.visibility = 'hidden';
  mirror.style.whiteSpace = 'pre-wrap';
  mirror.style.wordWrap = 'break-word';
  mirror.style.top = '0';
  mirror.style.left = '-9999px';
  mirror.textContent = element.value.slice(0, position);
  const marker = document.createElement('span');
  marker.textContent = element.value.slice(position) || '.';
  mirror.appendChild(marker);
  document.body.appendChild(mirror);
  try {
    return {
      top: marker.offsetTop - element.scrollTop + Number.parseFloat(style.borderTopWidth),
      left: marker.offsetLeft - element.scrollLeft + Number.parseFloat(style.borderLeftWidth),
      height: marker.offsetHeight,
    };
  } finally {
    document.body.removeChild(mirror);
  }
}

interface PhraseTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  clinicId: string;
  label?: string;
  error?: string;
  hint?: string;
  allowSaveSelection?: boolean;
}

export const PhraseTextarea = forwardRef<HTMLTextAreaElement, PhraseTextareaProps>(
  function PhraseTextarea(
    {
      clinicId,
      className,
      label,
      error,
      hint,
      id,
      value,
      onChange,
      onKeyDown,
      onSelect,
      onClick,
      onKeyUp,
      allowSaveSelection = true,
      disabled,
      ...props
    },
    forwardedRef,
  ) {
    const { t } = useTranslation();
    const { user, hasPermission } = useAuth();
    const canUsePhrases = hasPermission(clinicId, 'journal_phrases', 'view');
    const canCreateOwn = hasPermission(clinicId, 'journal_phrases', 'create');
    const canShare = hasPermission(clinicId, 'journal_phrases', 'update');
    const canAdminAll = hasPermission(clinicId, 'journal_phrases', 'delete');
    const currentUserId = user?.id ?? null;
    const inputId = id ?? props.name;
    const listId = useId();
    const innerRef = useRef<HTMLTextAreaElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const pendingCursor = useRef<number | null>(null);
    const pendingInsertTrigger = useRef<SlashTrigger | null>(null);
    const savedEditorCursor = useRef<number | null>(null);
    const phrasesQuery = useJournalPhrases(clinicId);
    const createPhrase = useCreateJournalPhrase(clinicId);
    const updatePhrase = useUpdateJournalPhrase(clinicId);
    const [trigger, setTrigger] = useState<SlashTrigger | null>(null);
    const [menuIndex, setMenuIndex] = useState(0);
    const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
    const [formOpen, setFormOpen] = useState(false);
    const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
    const [formError, setFormError] = useState<string | null>(null);
    const [formInitial, setFormInitial] = useState<Partial<JournalPhraseFormValues>>({});
    const [formInsertOnSave, setFormInsertOnSave] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [manageOpen, setManageOpen] = useState(false);
    const [selectedText, setSelectedText] = useState('');

    const setRefs = useCallback(
      (node: HTMLTextAreaElement | null) => {
        innerRef.current = node;
        if (typeof forwardedRef === 'function') forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      },
      [forwardedRef],
    );

    const popupPhrases = useMemo(
      () => phrasesForSlashPopup(phrasesQuery.data ?? [], currentUserId),
      [currentUserId, phrasesQuery.data],
    );
    const matches = useMemo(
      () => (trigger ? filterJournalPhrases(popupPhrases, trigger.query) : []),
      [popupPhrases, trigger],
    );
    const activeIndex = matches.length === 0 ? 0 : Math.min(menuIndex, matches.length - 1);

    useEffect(() => {
      setMenuIndex(0);
    }, [trigger?.query]);

    const updateMenuPosition = useCallback((el: HTMLTextAreaElement, slashIndex: number) => {
      const rect = el.getBoundingClientRect();
      const caret = getCaretCoordinates(el, slashIndex);
      const left = Math.min(rect.left + caret.left, window.innerWidth - 300);
      let top = rect.top + caret.top + caret.height + 4;
      if (top + 240 > window.innerHeight) {
        top = rect.top + caret.top - 8 - 200;
      }
      setMenuPos({ top: Math.max(8, top), left: Math.max(8, left) });
    }, []);

    const syncTrigger = useCallback(
      (el: HTMLTextAreaElement) => {
        const next =
          disabled || !canUsePhrases
            ? null
            : detectSlashTrigger(el.value, el.selectionStart ?? 0);
        setTrigger(next);
        if (next) updateMenuPosition(el, next.start);
        else setMenuPos(null);
      },
      [canUsePhrases, disabled, updateMenuPosition],
    );

    const captureSelection = (el: HTMLTextAreaElement) => {
      const start = el.selectionStart ?? 0;
      const end = el.selectionEnd ?? 0;
      setSelectedText(end > start ? el.value.slice(start, end) : '');
    };

    useLayoutEffect(() => {
      if (pendingCursor.current == null || !innerRef.current) return;
      const cursor = pendingCursor.current;
      pendingCursor.current = null;
      innerRef.current.focus();
      innerRef.current.setSelectionRange(cursor, cursor);
    }, [value]);

    useLayoutEffect(() => {
      if (!trigger || !innerRef.current) return;
      updateMenuPosition(innerRef.current, trigger.start);
    }, [trigger, value, updateMenuPosition]);

    useEffect(() => {
      if (!trigger) return;
      const onMouseDown = (event: MouseEvent) => {
        const target = event.target as Node;
        if (innerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
        setTrigger(null);
      };
      document.addEventListener('mousedown', onMouseDown);
      return () => document.removeEventListener('mousedown', onMouseDown);
    }, [trigger]);

    const emitValue = (next: string, cursor: number) => {
      pendingCursor.current = cursor;
      const target = innerRef.current;
      const event = {
        target: { value: next, name: props.name ?? '' },
        currentTarget: target ?? { value: next },
      } as ChangeEvent<HTMLTextAreaElement>;
      onChange?.(event);
    };

    const insertBody = (body: string) => {
      if (!trigger) return;
      const current = String(value ?? '');
      const result = insertPhraseText(current, trigger, body);
      setTrigger(null);
      emitValue(result.text, result.cursor);
    };

    const rememberEditorCursor = () => {
      const el = innerRef.current;
      savedEditorCursor.current = el?.selectionStart ?? 0;
      if (el) {
        el.focus();
        el.setSelectionRange(savedEditorCursor.current, savedEditorCursor.current);
      }
    };

    const restoreEditorFocus = useCallback(() => {
      const el = innerRef.current;
      const cursor = savedEditorCursor.current;
      requestAnimationFrame(() => {
        if (!el) return;
        el.focus();
        if (cursor != null) el.setSelectionRange(cursor, cursor);
      });
    }, []);

    const closeForm = useCallback(() => {
      setFormOpen(false);
      setEditingId(null);
      restoreEditorFocus();
    }, [restoreEditorFocus]);

    const closeManage = useCallback(() => {
      setManageOpen(false);
      restoreEditorFocus();
    }, [restoreEditorFocus]);

    const openCreateForm = (initial: Partial<JournalPhraseFormValues>, insertOnSave: boolean) => {
      pendingInsertTrigger.current = insertOnSave ? trigger : null;
      rememberEditorCursor();
      setFormMode('create');
      setEditingId(null);
      setFormInitial(initial);
      setFormInsertOnSave(insertOnSave);
      setFormError(null);
      setFormOpen(true);
      setTrigger(null);
    };

    const openEditForm = (phrase: JournalPhraseRow) => {
      rememberEditorCursor();
      setFormMode('edit');
      setEditingId(phrase.id);
      setFormInitial({
        shortcut: phrase.shortcut,
        title: phrase.title ?? '',
        body: phrase.body,
        visibility: phrase.visibility === 'shared' ? 'shared' : 'private',
      });
      setFormInsertOnSave(false);
      setFormError(null);
      setFormOpen(true);
      setTrigger(null);
    };

    const openManage = () => {
      rememberEditorCursor();
      setTrigger(null);
      setManageOpen(true);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (trigger) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setMenuIndex((index) => Math.min(index + 1, Math.max(matches.length - 1, 0)));
          return;
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          setMenuIndex((index) => Math.max(index - 1, 0));
          return;
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          setTrigger(null);
          return;
        }
        if ((event.key === 'Enter' || event.key === 'Tab') && matches[activeIndex]) {
          event.preventDefault();
          insertBody(matches[activeIndex].body);
          return;
        }
      }
      onKeyDown?.(event);
    };

    const handleFormSubmit = async (values: JournalPhraseFormValues) => {
      try {
        const visibility = canShare ? values.visibility : 'private';
        if (formMode === 'edit' && editingId) {
          await updatePhrase.mutateAsync({ id: editingId, ...values, visibility });
          setFormOpen(false);
          setEditingId(null);
          setFormError(null);
          toast.success(t('journal.phrases.updated'));
          restoreEditorFocus();
          return;
        }
        const created = await createPhrase.mutateAsync({ ...values, visibility });
        setFormOpen(false);
        setFormError(null);
        toast.success(t('journal.phrases.created'));
        const savedTrigger = pendingInsertTrigger.current;
        pendingInsertTrigger.current = null;
        if (formInsertOnSave && savedTrigger) {
          const current = String(value ?? '');
          const result = insertPhraseText(current, savedTrigger, created.body);
          emitValue(result.text, result.cursor);
        } else {
          restoreEditorFocus();
        }
      } catch (err) {
        setFormError(
          isPhraseShortcutConflict(err)
            ? t('journal.phrases.shortcut_conflict')
            : t('common.error'),
        );
      }
    };

    const menuOpen = Boolean(
      canUsePhrases && trigger && menuPos && !disabled && !formOpen && !manageOpen,
    );

    const textarea = (
      <textarea
        id={inputId}
        ref={setRefs}
        className={cn(
          TYPE.inputValue,
          'block w-full appearance-none rounded-md border border-ds-field px-3 py-2 placeholder:text-ds-placeholder shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal bg-ds-card text-ds-text',
          error && 'border-ds-red focus:border-ds-red focus:ring-ds-red',
          className,
        )}
        value={value}
        disabled={disabled}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
        aria-autocomplete="list"
        aria-controls={menuOpen ? listId : undefined}
        aria-expanded={menuOpen}
        {...props}
        onChange={(event) => {
          onChange?.(event);
          syncTrigger(event.currentTarget);
          captureSelection(event.currentTarget);
        }}
        onKeyDown={handleKeyDown}
        onKeyUp={(event) => {
          syncTrigger(event.currentTarget);
          captureSelection(event.currentTarget);
          onKeyUp?.(event);
        }}
        onClick={(event) => {
          syncTrigger(event.currentTarget);
          captureSelection(event.currentTarget);
          onClick?.(event);
        }}
        onSelect={(event) => {
          syncTrigger(event.currentTarget);
          captureSelection(event.currentTarget);
          onSelect?.(event);
        }}
      />
    );

    return (
      <div>
        {label && (
          <label htmlFor={inputId} className={cn('block', FORM_LABEL_TYPE)}>
            {label}
          </label>
        )}
        <div className={cn('relative', label && 'mt-1')}>{textarea}</div>
        {hint && !error && (
          <p id={`${inputId}-hint`} className={cn('mt-1', FORM_HINT_TYPE)}>
            {hint}
          </p>
        )}
        {error && (
          <p id={`${inputId}-error`} className={cn('mt-1', FORM_ERROR_TYPE)}>
            {error}
          </p>
        )}
        {allowSaveSelection && !disabled && canCreateOwn && (
          <div className="mt-1">
            <Button
              type="button"
              variant="link"
              disabled={!selectedText.trim()}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => openCreateForm({ body: selectedText, visibility: 'private' }, false)}
            >
              {t('journal.phrases.save_selection')}
            </Button>
          </div>
        )}
        {menuOpen &&
          menuPos &&
          createPortal(
            <div
              ref={menuRef}
              id={listId}
              role="listbox"
              aria-label={t('journal.phrases.list_label')}
              className={cn(
                'fixed w-80 overflow-hidden rounded-lg border border-ds-line bg-ds-card shadow-pop',
                JOURNAL_PHRASE_POPOVER_Z_CLASS,
              )}
              style={{ top: menuPos.top, left: menuPos.left }}
            >
              <div className="max-h-56 overflow-y-auto py-1">
                {matches.length === 0 ? (
                  <p className="px-3 py-2 text-sm text-ds-muted">
                    {t('journal.phrases.empty')}
                  </p>
                ) : (
                  matches.map((phrase, index) => (
                    <PhraseOption
                      key={phrase.id}
                      phrase={phrase}
                      active={index === activeIndex}
                      canEdit={
                        canManageJournalPhrase(phrase, currentUserId, canAdminAll) &&
                        (canAdminAll || canCreateOwn)
                      }
                      onHover={() => setMenuIndex(index)}
                      onInsert={() => insertBody(phrase.body)}
                      onEdit={() => openEditForm(phrase)}
                      sharedLabel={t('journal.phrases.shared')}
                      privateLabel={t('journal.phrases.private')}
                      editLabel={t('common.edit')}
                    />
                  ))
                )}
              </div>
              <div className="border-t border-ds-line">
                {canCreateOwn && (
                  <button
                    type="button"
                    className="block w-full px-3 py-2 text-left text-sm text-ds-teal hover:bg-ds-sunk hover:bg-ds-neutral-tint"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() =>
                      openCreateForm(
                        {
                          shortcut: trigger?.query ?? '',
                          visibility: 'private',
                        },
                        true,
                      )
                    }
                  >
                    {t('journal.phrases.create')}
                  </button>
                )}
                <button
                  type="button"
                  className="block w-full px-3 py-2 text-left text-sm text-ds-teal hover:bg-ds-sunk hover:bg-ds-neutral-tint"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={openManage}
                >
                  {t('journal.phrases.manage')}
                </button>
              </div>
            </div>,
            document.body,
          )}
        <JournalPhraseFormModal
          open={formOpen}
          mode={formMode}
          initial={formInitial}
          pending={createPhrase.isPending || updatePhrase.isPending}
          canShare={canShare}
          error={formError}
          onClose={closeForm}
          onSubmit={(values) => void handleFormSubmit(values)}
        />
        <ManageJournalPhrasesModal clinicId={clinicId} open={manageOpen} onClose={closeManage} />
      </div>
    );
  },
);

function PhraseOption({
  phrase,
  active,
  canEdit,
  onHover,
  onInsert,
  onEdit,
  sharedLabel,
  privateLabel,
  editLabel,
}: {
  phrase: JournalPhraseRow;
  active: boolean;
  canEdit: boolean;
  onHover: () => void;
  onInsert: () => void;
  onEdit: () => void;
  sharedLabel: string;
  privateLabel: string;
  editLabel: string;
}) {
  const visibility = phrase.visibility === 'shared' ? 'shared' : 'private';
  const badge = phraseVisibilityLabel(visibility, { private: privateLabel, shared: sharedLabel });
  return (
    <div
      role="option"
      aria-selected={active}
      className={cn(
        'flex w-full items-start gap-1 px-2 py-1.5 text-sm',
        active ? 'bg-ds-teal-tint' : 'hover:bg-ds-row-hover',
      )}
      onMouseEnter={onHover}
    >
      <button
        type="button"
        className="min-w-0 flex-1 px-1 text-left"
        onMouseDown={(event) => event.preventDefault()}
        onClick={onInsert}
      >
        <span className="font-medium text-ds-text">/{phrase.shortcut}</span>
        {phrase.title && (
          <span className="ml-1.5 text-ds-muted">{phrase.title}</span>
        )}
        <span className="mt-0.5 block truncate text-xs text-ds-muted">
          {phrase.body.split('\n')[0]}
        </span>
      </button>
      {canEdit && (
        <button
          type="button"
          className="mt-0.5 shrink-0 rounded p-1 text-ds-muted hover:bg-ds-neutral-tint hover:text-ds-text"
          aria-label={editLabel}
          title={editLabel}
          onMouseDown={(event) => event.preventDefault()}
          onClick={onEdit}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      )}
      <span
        className={cn(
          'mt-0.5 shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium',
          visibility === 'shared'
            ? 'bg-ds-teal-tint text-ds-teal'
            : 'bg-ds-neutral-tint text-ds-muted bg-ds-card',
        )}
      >
        {badge}
      </span>
    </div>
  );
}
